import { useCallback, useEffect, useMemo, useState } from 'react';

/** True on Apple platforms, where the palette binds Cmd-K rather than Ctrl-K. */
export function isAppleShortcutPlatform(): boolean {
  if (typeof navigator === 'undefined') return false;
  const platform =
    (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform ??
    navigator.platform ??
    navigator.userAgent ??
    '';
  return /mac|iphone|ipad|ipod/i.test(platform);
}

/**
 * Don't steal the shortcut from a field the user is typing in. Cmd-K is only
 * ours when the page isn't already using the keystroke for text entry —
 * notably the markdown editor, where Cmd-K inserts a link.
 */
function isTextEntryTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

export interface UseCommandPaletteResult {
  open: boolean;
  setOpen: (open: boolean) => void;
  openPalette: () => void;
  closePalette: () => void;
  /** `⌘K` on Apple platforms, `Ctrl K` elsewhere — for the header affordance. */
  shortcutLabel: string;
}

/**
 * Owns the global hotkey and the palette's open state.
 *
 * Split out of `CommandPalette` so the shell can render the header trigger and
 * the dialog as siblings while sharing one piece of state — and so the listener
 * is registered once per app rather than once per mounted palette.
 */
export function useCommandPalette(): UseCommandPaletteResult {
  const [open, setOpen] = useState(false);
  const isApple = useMemo(isAppleShortcutPlatform, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'k' && event.key !== 'K') return;
      // Cmd on macOS, Ctrl everywhere else. Accepting either would collide with
      // the OS-native meaning of the other modifier on each platform.
      const modifier = isApple ? event.metaKey : event.ctrlKey;
      if (!modifier || event.altKey) return;
      // While the palette is open its own input is the active element, and the
      // shortcut should still close it rather than being swallowed as typing.
      if (!open && isTextEntryTarget(event.target)) return;
      event.preventDefault();
      setOpen((previous) => !previous);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isApple, open]);

  const openPalette = useCallback(() => setOpen(true), []);
  const closePalette = useCallback(() => setOpen(false), []);

  return {
    open,
    setOpen,
    openPalette,
    closePalette,
    shortcutLabel: isApple ? '⌘K' : 'Ctrl K',
  };
}
